import React from 'react';
import {makeStyles} from '@material-ui/core';
import {Colors} from '../../styles/Colors';
import {TFunction} from 'react-i18next';
import {Column} from 'react-table';
import OverflowTooltip from '../../components/OverflowTooltip/OverflowTooltip';
import { PeersContactListItem } from '../../store/actions/accountDashboard/payloads';
import { Avatar } from '../../components/LogoutMenuItem/Avatar';
import * as crypto from 'crypto';

export const useStyles = makeStyles(() => ({
    tableContainer: {
        maxWidth: 1040,

        '& thead > tr > th:nth-of-type(1)': {
            justifyContent: 'center',
            justifyItems: 'center',
            textAlign: 'center',
            display: 'flex',
            paddingRight: '0px !important',
            paddingLeft: '0px !important',
        },
        
        '& tbody > tr > td:nth-of-type(1)': {
            whiteSpace: 'nowrap',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            paddingRight: '0px !important',
            paddingLeft: '0px !important',
        },
    },
    emailColumnContainer: {
        maxWidth: '100%',
    },
    emailColumn: {
        fontWeight: 700,
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        color: Colors.Gray5,
        opacity: 0.5,
    },
    extensionColumn: {
        color: Colors.Text,
        fontWeight: 700,
        fontSize: 16,
    },
    avatar: {
        marginTop: 4,
        textTransform: 'uppercase',
        '& div': {
            fontSize: '28px !important',
            fontWeight: 600,
            lineHeight: '16px',
            color: Colors.Gray6,
        },
        '& img': {
            zIndex: 1,
        },
    },
    nameContainer: {
        display: 'flex',
        flexDirection: 'row',
        maxWidth: '100%',
        minWidth: 0,
    },
    fullName: {
        color: Colors.Text,
        fontSize: 16,
        fontWeight: 400,
        lineHeight: '32px',
        marginLeft: 12,
        marginTop: 5
    },
    emptyColumnValue: {
        fontWeight: 700,
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        color: Colors.Gray5,
        opacity: 0.5,
    },
    overflowCell: {
        minWidth: 0,
    },
}));

export const generateColumns = (
    t: TFunction<string>,
    classes: ReturnType<typeof useStyles>,
): Column<PeersContactListItem>[] => {

    return [
        {
            accessor: 'extension_id',
            Header: t<string>('screens:accountPhoneBook.extension'),
            width: 1.2,
            Cell: function Cell(params) {
                return (
                    <div className={classes.overflowCell}>
                        <OverflowTooltip
                            dataQa="account-company-phone-book-extension_id"
                            text={params.row.original.extension_id}
                            tooltip={params.row.original.extension_id}
                            copy={false}
                            classes={{
                                text: classes.extensionColumn
                            }}
                        />
                    </div>
                );
            },
        },
        {
            accessor: 'firstname',
            Header: t<string>('common:name'),
            width: 3,
            Cell: function Cell(params) {
                let fullName = ((params.row.original.firstname ?? '') + ' ' + (params.row.original.lastname ?? '')).trim();
                if(!fullName)
                    fullName = params.row.original.extension_name;
                const emailMd5 = params.row.original.email === undefined
                        ? undefined
                        : crypto.createHash('md5').update(params.row.original.email).digest('hex');
                return (
                    <div className={classes.nameContainer}>
                        <div>
                            <Avatar
                                name={fullName}
                                emailMd5={emailMd5}
                                classes={classes.avatar}
                                size={32}
                            />
                        </div>
                        <div className={classes.overflowCell}>
                            <OverflowTooltip
                                dataQa="account-company-phone-book-name"
                                text={fullName}
                                tooltip={fullName}
                                copy={false}
                                classes={{
                                    text: classes.fullName
                                }}
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            accessor: 'email',
            Header: t<string>('common:email'),
            width: 2,
            Cell: function Cell(params) {
                if(!params.row.original.email?.length) {
                    return (
                        <div className={classes.emptyColumnValue}>
                            &mdash;
                        </div>);
                }
                return (
                    <div className={classes.emailColumnContainer}>
                        <OverflowTooltip
                            dataQa="account-company-phone-book-email"
                            text={params.row.original.email}
                            tooltip={params.row.original.email}
                            copy={false}
                            classes={{
                                text: classes.emailColumn
                            }}
                        />
                    </div>
                );
            },
        },
        {
            accessor: 'primary_group_name',
            Header: t<string>('screens:accountPhoneBook.primaryRingGroup'),
            width: 2,
            Cell: function Cell(params) {
                if(!params.row.original.primary_group_name?.length) {
                    return (
                        <div className={classes.emptyColumnValue}>
                            &mdash;
                        </div>);
                }
                return (
                    <div className={classes.overflowCell}>
                        <OverflowTooltip
                            dataQa="account-company-phone-book-primary_group_name"
                            text={params.row.original.primary_group_name}
                            tooltip={params.row.original.primary_group_name}
                            copy={false}
                        />
                    </div>
                );
            },
        },
    ];
};
